import { buildPhotoKey } from './photoKeys';
import { utcDayKey } from './dayBuckets';

// Pre-upload scan. Reads EXIF capture time from each file and buckets by
// UTC day. Non-image files are skipped and returned separately so the
// preflight can show the warning ("14 files skipped — not photos or videos").
//
// EXIF is a "best-effort" read: exifr throws on unreadable files, on which
// we fall through to file.lastModified. That fallback is called out in the
// preflight ("3 photos have no capture time — they'll be placed using
// their file date") so the user knows the day placement isn't authoritative.
//
// The parser is injectable so tests can run without a real binary parser.

const DEFAULT_EXIF_FIELDS = { pick: ['DateTimeOriginal', 'CreateDate'] };

async function defaultExifParser(file) {
  // Lazy import — exifr is ~200 KB and only needed the first time a user
  // opens the upload flow. Keeps the initial studio bundle lean.
  const exifr = (await import('exifr')).default;
  return exifr.parse(file, DEFAULT_EXIF_FIELDS);
}

function isImageFile(file) {
  if (!file) return false;
  if (typeof file.type === 'string' && file.type.startsWith('image/')) return true;
  const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
  return /\.(jpe?g|png|heic|heif|tiff?|webp|avif)$/i.test(name);
}

function pickCapturedAt(exif) {
  if (!exif) return null;
  const raw = exif.DateTimeOriginal || exif.CreateDate;
  if (!raw) return null;
  if (raw instanceof Date) {
    const ms = raw.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

// Scan a File[] and return everything the preflight needs. Never throws —
// per-file EXIF parse failures are absorbed into the fallback bucket.
export async function scanPhotos(files, { parseExif = defaultExifParser } = {}) {
  const entries = [];
  const skippedNonImages = [];
  const dayBuckets = new Map();
  const seenKeys = new Set();
  let missingExif = 0;
  let totalBytes = 0;

  for (const file of Array.from(files || [])) {
    if (!isImageFile(file)) {
      skippedNonImages.push(file);
      continue;
    }

    let capturedAt = null;
    let capturedAtSource = 'fileModified';
    try {
      const exif = await parseExif(file);
      const fromExif = pickCapturedAt(exif);
      if (fromExif != null) {
        capturedAt = fromExif;
        capturedAtSource = 'exif';
      }
    } catch (_err) {
      // fall through to file.lastModified
    }

    if (capturedAt == null) {
      missingExif += 1;
      capturedAt = Number.isFinite(file.lastModified) ? file.lastModified : null;
      capturedAtSource = 'fileModified';
    }

    // Key on the STABLE EXIF capture time only. When capturedAt fell back to
    // file.lastModified (no EXIF) we pass nothing, so the jittery mtime never
    // enters the key and re-drops of the same folder still dedup.
    const stableCapturedAt = capturedAtSource === 'exif' ? capturedAt : null;
    const photoKey = buildPhotoKey(file, { capturedAtMs: stableCapturedAt });
    // Dedup within the drop itself — a user can pick overlapping folders.
    if (seenKeys.has(photoKey)) continue;
    seenKeys.add(photoKey);

    const dayKey = utcDayKey(capturedAt);
    const entry = {
      file,
      photoKey,
      capturedAt,
      capturedAtSource,
      dayKey,
      sizeBytes: Number.isFinite(file.size) ? file.size : 0,
    };
    entries.push(entry);
    totalBytes += entry.sizeBytes;

    if (!dayBuckets.has(dayKey)) dayBuckets.set(dayKey, []);
    dayBuckets.get(dayKey).push(entry);
  }

  return {
    entries,
    dayBuckets,
    skippedNonImages,
    missingExif,
    totals: {
      photoCount: entries.length,
      totalBytes,
      dayCount: [...dayBuckets.keys()].filter((k) => k !== 'unknown').length,
    },
  };
}
